import { makeStyles, Typography, Switch, FormLabel } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useEffect, useState } from 'react';
import usersAPI, { UserPreferences } from '../../api/users';
import { SnackMessage } from '../../components/common';
import PageLoader from '../../components/common/page-loader';
import { COLORS } from '../../theme/variables';

interface ToggleProps {
  name: keyof UserPreferences;
  value: boolean;
  label: string;
  handleChange: (key: keyof UserPreferences, value: boolean) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  container: {
    maxWidth: 420,
  },
  title: {
    margin: '0 0 20px',
  },
  toggleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  toggleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  label: {
    color: COLORS.COLOR_TEXT_BASE,
  },
});

const Toggle: FC<ToggleProps> = ({
  name,
  value,
  label,
  disabled,
  handleChange,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.toggleWrapper}>
      <div className={classes.toggleContainer}>
        <FormLabel htmlFor={name} className={classes.label}>
          {label}
        </FormLabel>
      </div>
      <Switch
        checked={value}
        color='primary'
        onChange={(_, checked) => handleChange(name, checked)}
        disabled={disabled}
        id={name}
        name={name}
      />
    </div>
  );
};

function TenantNotificationSettingsPage() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await usersAPI.getPreferences();
      setPreferences(data);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      enqueueSnackbar('Failed to load notification preferences', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleChange = useCallback(
    async (key: keyof UserPreferences, value: boolean) => {
      if (!preferences) return;

      try {
        const updatedPreferences: UserPreferences = {
          ...preferences,
          [key]: value,
        };
        const updated = await usersAPI.updatePreferences(updatedPreferences);
        setPreferences(updated);
        const message = value
          ? `${getToggleLabel(key)} is enabled`
          : `${getToggleLabel(key)} is disabled`;
        enqueueSnackbar(message, {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        const message = value
          ? `An error occurred while enabling ${getToggleLabel(
              key,
            )}. Please, try again.`
          : `An error occurred while disabling ${getToggleLabel(
              key,
            )}. Please, try again.`;

        enqueueSnackbar(message, {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, preferences],
  );

  if (isLoading) {
    return <PageLoader />;
  }

  if (!preferences) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Typography className={classes.title} variant='h4'>
        Notification Settings
      </Typography>
      <div className={classes.toggleList}>
        <Toggle
          name='sessionUpdatesNotificationsEnabled'
          value={preferences.sessionUpdatesNotificationsEnabled}
          label='Session Updates Notifications'
          handleChange={handleChange}
        />
        <Toggle
          name='mentionsNotificationsEnabled'
          value={preferences.mentionsNotificationsEnabled}
          label='Mentions Notifications'
          handleChange={handleChange}
        />
        <Toggle
          name='schedulingNotificationsEnabled'
          value={preferences.schedulingNotificationsEnabled}
          label='Session Scheduling Notifications'
          handleChange={handleChange}
        />
        <Toggle
          name='otherNotificationsEnabled'
          value={preferences.otherNotificationsEnabled}
          label='Other Notifications'
          handleChange={handleChange}
        />
      </div>
    </div>
  );
}

function getToggleLabel(key: keyof UserPreferences): string {
  switch (key) {
    case 'sessionUpdatesNotificationsEnabled':
      return 'Session Updates Notifications';
    case 'mentionsNotificationsEnabled':
      return 'Mentions Notifications';
    case 'schedulingNotificationsEnabled':
      return 'Session Scheduling Notifications';
    case 'otherNotificationsEnabled':
      return 'Other Notifications';
    default:
      return 'Notification';
  }
}

export default TenantNotificationSettingsPage;
